import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material';
import firebase from 'firebase/compat/app';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFirestore } from '../hooks/useFirebase';
import {
  memberWorksJotaiAtom,
  passwordHistoriesJotaiAtom,
  popupAlertAtom,
  teamMembersJotaiAtom
} from '../jotai/atoms';
import { analytics } from '../utils/analytics';
import { AutocompleteTags } from './AutocompleteTags';
import { LoadingPage } from './LoadingPage';
import { UnmountOnChange } from './UnmountOnChange';
import { UpdatePasswordDialog } from './UpdatePasswordDialog';
import { UserProfile } from './UserProfile';

type Params = {
  teamId: string;
  uid: string;
};

const styles = {
  bar: {
    paddingLeft: 4,
    borderBottomColor: 'divider',
    borderBottomStyle: 'solid',
    borderWidth: 1,
    color: 'text.secondary'
  },
  content: {
    padding: 4,
    '& > *': {
      marginBottom: 4
    }
  },
  autocomplete: {
    maxWidth: 500
  },
  block: {
    display: 'block'
  },
  worksRoot: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  worksContainer: {
    width: 240,
    overflow: 'hidden',
    marginRight: 2,
    '& > img': {
      width: '100%'
    }
  },
  worksButton: {
    paddingLeft: 0,
    paddingRight: 0,
    textDecoration: 'none'
  },
  worksDetail: {
    display: 'flex',
    alignItems: 'center'
  },
  passwordHistories: {
    '& > div': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  timestamp: {
    marginRight: 2
  }
} as const;

const img404 = 'https://cdn.hackforplay.xyz/ogp-image/not-found-bomy.png';

/**
 * ダッシュボードの「メンバー」からメンバー詳細を開いたページ
 */
export function DashboardMember() {
  // メンバーを取得
  const { teamId, uid } = useParams<Params>();
  const member = useAtomValue(teamMembersJotaiAtom({ teamId, uid }));

  const firestore = useFirestore();
  const [updated, setUpdated] = React.useState<Partial<IMember>>({});
  const setPopupAlert = useSetAtom(popupAlertAtom);
  const setMember = useSetAtom(teamMembersJotaiAtom({ teamId, uid }));
  const updateMember = React.useCallback(
    async (payload: Partial<IMember>) => {
      setEditing(false);
      // チームメンバーのドキュメントを変更する
      // disableCurrentPage=true にした時だけ、現在の currentPage を削除する
      const updated = payload.disableCurrentPage
        ? {
            ...payload,
            currentPage: firebase.firestore.FieldValue.delete()
          }
        : payload;
      try {
        await firestore
          .collection('teams')
          .doc(teamId)
          .collection('members')
          .doc(uid)
          .update(updated);
        // ストアの情報を上書きする
        setMember((curr: IMember | undefined) =>
          curr ? { ...curr, ...payload } : undefined
        );
      } catch (error) {
        setPopupAlert({
          severity: 'error',
          children: '更新に失敗しました。再読み込みしてください'
        });
        setEditing(true);
      }
    },
    [teamId, uid, firestore, setPopupAlert, setMember]
  );

  // 名前を変更する
  const [editing, setEditing] = React.useState(false);

  return (
    <>
      <Box sx={styles.bar}>
        <Button color="inherit" component={Link} to={`/${teamId}/members`}>
          <ArrowBack />
          メンバー一覧に戻る
        </Button>
      </Box>
      {member ? (
        <Box sx={styles.content}>
          <UserProfile uid={uid} />
          <UnmountOnChange value={member?.name}>
            <TextField
              label="管理用の名前"
              disabled={!editing}
              defaultValue={updated.name || member?.name || ''}
              onChange={({ target }) =>
                setUpdated(p => ({ ...p, name: target.value }))
              }
            />
          </UnmountOnChange>
          <UnmountOnChange value={member?.tags}>
            <AutocompleteTags
              disabled={!editing}
              defaultValue={member?.tags || []}
              teamId={teamId}
              onChange={(e, tags) => setUpdated(p => ({ ...p, tags }))}
              sx={styles.autocomplete}
            />
          </UnmountOnChange>
          <Button
            variant="contained"
            color="primary"
            onClick={() => (editing ? updateMember(updated) : setEditing(true))}
          >
            {editing ? '完了' : '編集する'}
          </Button>
          <Typography variant="h5">最近のステージ</Typography>
          <Typography variant="body1" color="text.secondary">
            管理者はメンバーの未公開作品を閲覧できます。直近５個まで表示しています。
          </Typography>
          <React.Suspense fallback={<LoadingPage />}>
            <MemberWorks />
          </React.Suspense>
          <Typography variant="h5">パスワード</Typography>
          <Typography variant="body1" color="text.secondary">
            管理者はメンバーのパスワードを変更できます。変更した場合はここにその履歴が表示されます。
          </Typography>
          <React.Suspense fallback={<LoadingPage />}>
            <PasswordHistories />
          </React.Suspense>
          <UpdatePasswordDialog teamId={teamId} uid={uid} />
          <TextField
            disabled
            label="ログイン ID"
            value={member?.loginId || ''}
            helperText={
              member?.loginId
                ? 'ログイン ID は変更できません'
                : '表示できません'
            }
            sx={styles.block}
          />
          <Typography variant="h5">高度な設定</Typography>
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(member?.disableCurrentPage)}
                  onChange={(e, checked) =>
                    updateMember({
                      disableCurrentPage: checked // disableCurrentPage=true にすると currentPage を更新しない
                    })
                  }
                />
              }
              label="このメンバーが見ているページを秘密にする"
            />
          </div>
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!member?.rights}
                  onChange={(e, checked) => {
                    checked
                      ? analytics.enableMember()
                      : analytics.disableMember();
                    updateMember({
                      rights: !checked
                    });
                  }}
                />
              }
              label="このメンバーを一時的に休止状態にする"
            />
            <Typography variant="body2" color="text.secondary">
              休止状態のメンバーには、法人プランの特典をご利用いただけません。特典に関する詳細は
              <a
                href="https://www.hackforplay.xyz/pages/post/enterprise"
                target="_blank"
                rel="noopener noreferrer"
              >
                こちら
              </a>
              をご覧ください
            </Typography>
          </div>
        </Box>
      ) : (
        <Typography variant="h6">存在しないユーザーです</Typography>
      )}
    </>
  );
}

function MemberWorks() {
  const { teamId, uid } = useParams<Params>();
  const params = React.useMemo(() => ({ teamId, uid }), [teamId, uid]);
  const works = useAtomValue(memberWorksJotaiAtom(params));

  return (
    <>
      <Box sx={styles.worksRoot}>
        {works && works.length > 0 ? (
          <>
            {works.map((work: any, i: number) => (
              <Box key={i} sx={styles.worksContainer}>
                <img src={work.thumbnailUrl || img404} alt="" />
                <Button
                  color="primary"
                  href={work.path && `https://www.hackforplay.xyz/${work.path}`}
                  target="_blank"
                  sx={styles.worksButton}
                >
                  {work.title || <i>タイトルがついていません</i>}
                </Button>
                <Chip
                  size="small"
                  label={work.visibility === 'public' ? '公開' : '限定公開'}
                />
                <Typography variant="body2" color="text.secondary">
                  {toString(work.createdAt, 'に作成')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {toString(work.updatedAt, 'に更新')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {toString(work.publishedAt, 'に公開')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {toString(work.deletedAt, 'に削除')}
                </Typography>
              </Box>
            ))}
          </>
        ) : works?.length === 0 ? (
          <Typography variant="body1">まだステージがありません</Typography>
        ) : (
          <Typography variant="body1" color="text.secondary">
            読み込み中です
          </Typography>
        )}
      </Box>
    </>
  );
}

function PasswordHistories() {
  const { teamId, uid } = useParams<Params>();
  const params = React.useMemo(() => ({ teamId, uid }), [teamId, uid]);
  const passwordHistories = useAtomValue(passwordHistoriesJotaiAtom(params));

  return passwordHistories ? (
    passwordHistories.length > 0 ? (
      <Box sx={styles.passwordHistories}>
        {passwordHistories.map((item: any, i: number) => (
          <div key={i}>
            <Typography variant="body1" sx={styles.timestamp}>
              {item.updatedAt.toDate().toLocaleString()}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              変更者：
            </Typography>
            <UserProfile uid={item.updatedBy} />
          </div>
        ))}
      </Box>
    ) : (
      <Typography variant="body1">
        パスワードが変更された履歴はありません。
      </Typography>
    )
  ) : (
    <Typography variant="body1" color="text.secondary">
      読み込み中です
    </Typography>
  );
}

function toString(date: any, suffix: string) {
  const ms = (date?._seconds || 0) * 1000 + (date?._nanoseconds || 0) / 1000000;
  return ms ? new Date(ms).toLocaleString() + suffix : '';
}
